import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PaperPlaneRight, Robot, User, Sparkle, Lightning } from "@phosphor-icons/react";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

const SUGGESTIONS = [
  "Which customer owes us the most money?",
  "How many orders are still in transit?",
  "Show me unpaid invoices over PKR 10,000",
  "Which supplier did we pay the most this month?",
  "What is my gross margin on orders this month?",
  "Summarise Gulzar Sb's account",
];

export default function AIAssistant() {
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content:
        "Hi — I'm your business analyst. Ask me anything about your customers, suppliers, orders, balances, or recent transactions. I have access to your live data; financial figures are shown to Admin / Accounts / Super Admin only.",
    },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const sessionRef = useRef(`session-${Date.now()}`);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, busy]);

  const send = async (text) => {
    const message = (text ?? input).trim();
    if (!message || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: message }]);
    setBusy(true);
    try {
      const { data } = await api.post("/ai/chat", {
        message, session_id: sessionRef.current,
      });
      setMessages((m) => [...m, { role: "assistant", content: data.response }]);
    } catch (e) {
      const err = e.response?.data?.detail || "AI unavailable";
      setMessages((m) => [...m, { role: "assistant", content: `*Error: ${err}*` }]);
      toast.error(err);
    } finally { setBusy(false); }
  };

  return (
    <div className="flex flex-col h-[calc(100vh-7rem)]" data-testid="ai-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="label-mini flex items-center gap-2"><Sparkle size={11} /> AI Analyst</div>
          <h1 className="text-3xl font-bold tracking-tight mt-1">Business Q&amp;A</h1>
        </div>
        <div className="text-[11px] text-zinc-500 font-mono">
          session: {sessionRef.current.slice(-8)}
        </div>
      </div>

      <div className="flex-1 mt-4 card-sharp p-0 flex flex-col min-h-0">
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {messages.map((m, i) => (
            <Bubble key={i} role={m.role} content={m.content} />
          ))}
          {busy && (
            <div className="flex items-center gap-2 text-xs text-zinc-500" data-testid="ai-thinking">
              <div className="h-6 w-6 grid place-items-center bg-black text-white"><Robot size={12} /></div>
              <div className="flex gap-1">
                <span className="h-1.5 w-1.5 bg-zinc-400 rounded-full animate-pulse" />
                <span className="h-1.5 w-1.5 bg-zinc-400 rounded-full animate-pulse" style={{ animationDelay: "0.15s" }} />
                <span className="h-1.5 w-1.5 bg-zinc-400 rounded-full animate-pulse" style={{ animationDelay: "0.3s" }} />
              </div>
              <span>Reading your ledgers…</span>
            </div>
          )}
          <div ref={endRef} />
        </div>

        {messages.length <= 1 && (
          <div className="px-5 pt-1 pb-3 border-t border-zinc-100 bg-zinc-50">
            <div className="label-mini mb-2 flex items-center gap-1"><Lightning size={11} /> Try asking</div>
            <div className="flex flex-wrap gap-2">
              {SUGGESTIONS.map((s) => (
                <button
                  key={s}
                  onClick={() => send(s)}
                  data-testid={`suggestion-${s.slice(0, 10)}`}
                  className="text-xs px-3 py-1.5 bg-white border border-zinc-200 hover:border-black hover:bg-zinc-50 rounded-sm"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        <form
          onSubmit={(e) => { e.preventDefault(); send(); }}
          className="border-t border-zinc-200 p-3 flex items-center gap-2 bg-white"
        >
          <Input
            data-testid="ai-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask anything about your business…"
            className="rounded-sm h-10"
            disabled={busy}
            autoFocus
          />
          <Button
            type="submit"
            data-testid="ai-send-btn"
            disabled={busy || !input.trim()}
            className="rounded-sm h-10 px-4 bg-black text-white hover:bg-zinc-800"
          >
            <PaperPlaneRight size={14} className="mr-1" /> Send
          </Button>
        </form>
      </div>
    </div>
  );
}

function Bubble({ role, content }) {
  const isUser = role === "user";
  return (
    <div className={`flex gap-3 ${isUser ? "flex-row-reverse" : ""}`} data-testid={`bubble-${role}`}>
      <div
        className={`h-7 w-7 grid place-items-center shrink-0 ${
          isUser ? "bg-zinc-200 text-zinc-700" : "bg-black text-white"
        }`}
      >
        {isUser ? <User size={13} /> : <Robot size={13} />}
      </div>
      <div
        className={`max-w-[80%] text-sm leading-relaxed border ${
          isUser
            ? "bg-zinc-100 border-zinc-200 text-zinc-900"
            : "bg-white border-zinc-200"
        } p-3 rounded-sm`}
      >
        <div className="ai-markdown">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
        </div>
      </div>
    </div>
  );
}
